import React from 'react';
import { HourlyWeather } from '../models/WeatherData';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../contexts/SettingsContext';
import {
  convertTemperature,
  convertWindSpeed,
  convertPressure,
  convertPrecipitation,
  getTemperatureUnit,
  getWindSpeedUnit,
  getPressureUnit,
  getPrecipitationUnit,
  getVisibilityUnit,
} from '../utils/unitConversion';

interface HourlyWeatherGridProps {
  hourlyWeather: HourlyWeather[];
}

const HourlyWeatherGrid: React.FC<HourlyWeatherGridProps> = ({ hourlyWeather }) => {
  const { t } = useLanguage();
  const { unitSystem } = useSettings();
  
  const getWeatherIcon = (code: number): string => {
    if (code === 0) return '☀️';
    if (code <= 2) return '🌤️';
    if (code === 3) return '☁️';
    if (code >= 45 && code <= 48) return '🌫️';
    if (code >= 51 && code <= 55) return '🌦️';
    if (code >= 61 && code <= 65) return '🌧️';
    if (code >= 71 && code <= 75) return '❄️';
    if (code >= 80 && code <= 82) return '🌧️';
    if (code >= 95) return '⛈️';
    return '🌤️';
  };

  // Get translated weather description (currently unused but available for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getWeatherDescription = (code: number): string => {
    const descriptions: { [key: number]: string } = {
      0: t.weatherClear,
      1: t.weatherMostlyClear,
      2: t.weatherPartlyCloudy,
      3: t.weatherCloudy,
      45: t.weatherFog,
      48: t.weatherRimeFog,
      51: t.weatherLightDrizzle,
      53: t.weatherModerateDrizzle,
      55: t.weatherDenseDrizzle,
      61: t.weatherLightRain,
      63: t.weatherModerateRain,
      65: t.weatherHeavyRain,
      71: t.weatherLightSnow,
      73: t.weatherModerateSnow,
      75: t.weatherHeavySnow,
      80: t.weatherLightShowers,
      81: t.weatherModerateShowers,
      82: t.weatherHeavyShowers,
      95: t.weatherThunderstorm,
      96: t.weatherThunderstormHail
    };
    return descriptions[code] || t.weatherUnknown;
  };

  const getWindDirectionText = (deg: number): string => {
    // 8-direction compass using translations
    const directions = [t.north, t.northeast, t.east, t.southeast, t.south, t.southwest, t.west, t.northwest];
    const index = Math.round(deg / 45) % 8;
    return directions[index];
  };

  const getWindDirectionArrow = (deg: number): string => {
    // Wind direction indicates where wind comes FROM
    // Add 180° to show where it's blowing TO
    const direction = (deg + 180) % 360;
    
    // Return arrow based on direction (8 directions)
    if (direction >= 337.5 || direction < 22.5) return '↑';    // N
    if (direction >= 22.5 && direction < 67.5) return '↗';     // NE
    if (direction >= 67.5 && direction < 112.5) return '→';    // E
    if (direction >= 112.5 && direction < 157.5) return '↘';   // SE
    if (direction >= 157.5 && direction < 202.5) return '↓';   // S
    if (direction >= 202.5 && direction < 247.5) return '↙';   // SW
    if (direction >= 247.5 && direction < 292.5) return '←';   // W
    if (direction >= 292.5 && direction < 337.5) return '↖';   // NW
    return '↓';
  };

  if (!hourlyWeather || hourlyWeather.length === 0) {
    return null;
  }

  // Check if any hour is current hour
  const currentHour = new Date().getHours();
  const today = new Date().toISOString().split('T')[0];

  return (
    <div id="hourly-weather" className="hourly-container">
      <div className="rounded-2xl sm:rounded-3xl bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] p-3 sm:p-5 overflow-hidden">
        <div className="mb-3 sm:mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
            🕰️ <span className="hidden sm:inline">{t.hourlyForecast}</span><span className="sm:hidden">{t.hourlyForecastShort}</span>
          </h3>
        </div>
        
        {/* Compact table view with scroll indicators */}
        <div className="relative -mx-3 sm:-mx-5">
          {/* Left gradient fade */}
          <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent pointer-events-none z-10 opacity-0 transition-opacity" id="scroll-left-indicator"></div>
          
          {/* Right gradient fade */}
          <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-slate-900 via-slate-900/80 to-transparent pointer-events-none z-10 opacity-0 transition-opacity" id="scroll-right-indicator"></div>
          
          <div 
            className="overflow-x-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent"
            onScroll={(e) => {
              const target = e.target as HTMLDivElement;
              const leftIndicator = document.getElementById('scroll-left-indicator');
              const rightIndicator = document.getElementById('scroll-right-indicator');
              
              if (leftIndicator && rightIndicator) {
                const hasScroll = target.scrollWidth > target.clientWidth;
                
                if (hasScroll) {
                  // Show left indicator if scrolled right
                  leftIndicator.style.opacity = target.scrollLeft > 10 ? '1' : '0';
                  
                  // Show right indicator if not at the end
                  const isAtEnd = target.scrollLeft + target.clientWidth >= target.scrollWidth - 10;
                  rightIndicator.style.opacity = isAtEnd ? '0' : '1';
                } else {
                  // No scroll needed, hide both
                  leftIndicator.style.opacity = '0';
                  rightIndicator.style.opacity = '0';
                }
              }
            }}
            ref={(el) => {
              // Initialize scroll indicators on mount
              if (el) {
                const leftIndicator = document.getElementById('scroll-left-indicator');
                const rightIndicator = document.getElementById('scroll-right-indicator');
                
                if (leftIndicator && rightIndicator) {
                  const hasScroll = el.scrollWidth > el.clientWidth;
                  rightIndicator.style.opacity = hasScroll ? '1' : '0';
                  leftIndicator.style.opacity = '0';
                }
              }
            }}
          >
            <table className="w-full text-xs sm:text-sm border-collapse">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-2 px-2 sm:px-3 text-slate-400 font-semibold whitespace-nowrap">{t.time}<br/><span className="text-[10px]">kl.</span></th>
                <th className="text-center py-2 px-2 text-slate-400 font-semibold whitespace-nowrap">{t.weather}<br/><span className="text-[10px]">{getTemperatureUnit(unitSystem)}</span></th>
                <th className="text-right py-2 px-2 text-slate-400 font-semibold whitespace-nowrap">{t.precipitation}<br/><span className="text-[10px]">{getPrecipitationUnit(unitSystem)}</span></th>
                <th className="text-right py-2 px-2 sm:px-3 text-slate-400 font-semibold whitespace-nowrap">{t.wind} ({t.windGust})<br/><span className="text-[10px]">{getWindSpeedUnit(unitSystem)}</span></th>
                <th className="text-right py-2 px-2 text-slate-400 font-semibold whitespace-nowrap">{t.feelsLike}<br/><span className="text-[10px]">{getTemperatureUnit(unitSystem)}</span></th>
                <th className="text-right py-2 px-2 text-slate-400 font-semibold whitespace-nowrap">{t.humidity}<br/><span className="text-[10px]">%</span></th>
                <th className="text-right py-2 px-2 text-slate-400 font-semibold whitespace-nowrap">{t.pressure}<br/><span className="text-[10px]">{getPressureUnit(unitSystem)}</span></th>
                <th className="text-right py-2 px-2 text-slate-400 font-semibold whitespace-nowrap">{t.visibility}<br/><span className="text-[10px]">{getVisibilityUnit(unitSystem)}</span></th>
              </tr>
            </thead>
            <tbody>
              {hourlyWeather.slice(0, 24).map((hour, index) => {
                const isCurrentHour = hour.date === today && hour.hour === currentHour;
                const rowClass = isCurrentHour 
                  ? 'bg-blue-500/10 border-l-2 border-blue-500' 
                  : index % 2 === 0 ? 'bg-white/[0.02]' : 'bg-transparent';
                
                // Calculate "feels like" temperature (simplified wind chill)
                const feelsLike = hour.windSpeed > 1.3 
                  ? Math.round(13.12 + 0.6215 * hour.temperature - 11.37 * Math.pow(hour.windSpeed * 3.6, 0.16) + 0.3965 * hour.temperature * Math.pow(hour.windSpeed * 3.6, 0.16))
                  : Math.round(hour.temperature);
                
                // Format visibility with unit conversion
                // Note: hour.visibility is already in km (converted in WeatherService)
                const visibilityKm = hour.visibility || 10; // Default to 10km if undefined
                let visibility: string;
                const unit = getVisibilityUnit(unitSystem);
                
                if (unitSystem === 'imperial') {
                  // Convert km to miles for imperial
                  const visibilityMi = visibilityKm * 0.621371;
                  visibility = visibilityMi >= 31 ? `>31 ${unit}` : 
                               visibilityMi >= 12 ? `12 – 31 ${unit}` : 
                               visibilityMi >= 6 ? `6 – 12 ${unit}` : 
                               visibilityMi >= 2.5 ? `2.5 – 6 ${unit}` : 
                               visibilityMi >= 0.6 ? `0.6 – 2.5 ${unit}` : `<0.6 ${unit}`;
                } else {
                  visibility = visibilityKm >= 50 ? `>50 ${unit}` : 
                               visibilityKm >= 20 ? `20 – 50 ${unit}` : 
                               visibilityKm >= 10 ? `10 – 20 ${unit}` : 
                               visibilityKm >= 4 ? `4 – 10 ${unit}` : 
                               visibilityKm >= 1 ? `1 – 4 ${unit}` : `<1 ${unit}`;
                }
                
                return (
                  <tr key={index} className={`${rowClass} border-b border-white/5 hover:bg-white/[0.05] transition-colors`}>
                    <td className="py-2 px-2 sm:px-3 text-white font-medium whitespace-nowrap">
                      {hour.hour.toString().padStart(2, '0')}
                    </td>
                    <td className="py-2 px-2 text-center">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-lg">{getWeatherIcon(hour.weather_code || 0)}</span>
                        <span className="text-white font-medium">{Math.round(convertTemperature(hour.temperature, unitSystem))}°</span>
                      </div>
                    </td>
                    <td className="py-2 px-2 text-right text-slate-300">
                      {hour.precipitation > 0 ? convertPrecipitation(hour.precipitation, unitSystem).toFixed(unitSystem === 'imperial' ? 2 : 1) : '0'}
                    </td>
                    <td className="py-2 px-2 sm:px-3 text-right">
                      <span className="text-slate-300 whitespace-nowrap">
                        {getWindDirectionText(hour.windDirection || 0)} {getWindDirectionArrow(hour.windDirection || 0)} {Math.round(convertWindSpeed(hour.windSpeed, unitSystem))} ({hour.windGust ? Math.round(convertWindSpeed(hour.windGust, unitSystem)) : Math.round(convertWindSpeed(hour.windSpeed, unitSystem))})
                      </span>
                    </td>
                    <td className="py-2 px-2 text-right text-slate-300 font-medium">
                      {Math.round(convertTemperature(feelsLike, unitSystem))}°
                    </td>
                    <td className="py-2 px-2 text-right text-slate-300">
                      {Math.round(hour.humidity)} %
                    </td>
                    <td className="py-2 px-2 text-right text-slate-300">
                      {unitSystem === 'imperial' ? convertPressure(hour.pressure, unitSystem).toFixed(2) : Math.round(convertPressure(hour.pressure, unitSystem))}
                    </td>
                    <td className="py-2 px-2 text-right text-slate-300">
                      {visibility}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HourlyWeatherGrid;
